import {
  NgClass,
  NgForOf,
  NgIf,
  NgSwitch,
  NgSwitchCase,
} from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FirmwareService } from '../../api/backend/services/firmware/firmware.service';
import { MetaversionService } from '../../api/backend/services/metaversion/metaversion.service';
import { JobsService } from '../../api/jobs.service';
import { StatsDeleteFirmwareResponse } from '../../models/firmware';
import {
  DeleteFirmwareAction,
  TerminalState,
} from '../../models/firmware/delete-firmware';
import { Job } from '../../models/job';
import { MetaVersion } from '../../models/metaversion/meta-version';

@Component({
  selector: 'app-confirm-delete-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgForOf,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    RouterLink,
  ],
  templateUrl: './confirm-delete-dialog.component.html',
  styleUrls: ['./confirm-delete-dialog.component.scss'],
})
export class ConfirmDeleteDialogComponent implements OnInit {
  protected readonly modal = inject(NgbActiveModal);
  protected readonly firmwareService = inject(FirmwareService);
  protected readonly metaversionService = inject(MetaversionService);
  protected readonly jobsService = inject(JobsService);
  protected readonly toastrService = inject(ToastrService);

  firmwareId!: string;
  deleteStats?: StatsDeleteFirmwareResponse;
  actions: DeleteFirmwareAction[] = [];
  icons = {
    pending: 'far fa-hourglass',
    working: 'fas fa-spinner',
    failed: 'fas fa-times',
    done: 'fas fa-check',
  };

  loading = true;
  deleting = false;
  done = false;

  ngOnInit(): void {
    this.firmwareService
      .getDeleteStatistics(this.firmwareId)
      .pipe(
        catchError((err): Observable<undefined> => {
          console.error(err);
          this.toastrService.error('Failed to retrieve statistics');
          return of();
        }),
      )
      .subscribe((stats: StatsDeleteFirmwareResponse | undefined) => {
        this.loading = false;

        if (!stats) return;

        this.deleteStats = stats;
        this.actions = [
          {
            step: 'DEACTIVATE_METAVERSION',
            state: 'pending',
            subSteps: stats.metaversions.map((_metaversion: MetaVersion) => {
              return { id: _metaversion.id!, state: 'pending' };
            }),
          },
          {
            step: 'CANCEL_DEPLOYMENT',
            state: 'pending',
            subSteps: stats.jobs.map((_job: Job) => {
              return { id: _job.jobId!, state: 'pending' };
            }),
          },
          {
            step: 'DELETE_FIRMWARE',
            state: 'pending',
          },
        ];
      });
  }

  async confirmDeletion(): Promise<void> {
    this.deleting = true;
    await this.executeActionsSequentially();
  }

  async executeActionsSequentially(): Promise<void> {
    for (const action of this.actions) {
      action.state = 'working';

      switch (action.step) {
        case 'DEACTIVATE_METAVERSION':
          action.state = await this.stepDeactivateMetaversion(action);
          break;

        case 'CANCEL_DEPLOYMENT':
          action.state = await this.stepCancelDeployment(action);
          break;

        case 'DELETE_FIRMWARE':
          action.state = await this.stepDeleteFiles(action);
          break;
      }

      if (action.state === 'failed') {
        break; // stop process
      }
    }

    this.done = this.isDone();
  }

  async stepDeactivateMetaversion(
    action: DeleteFirmwareAction,
  ): Promise<TerminalState> {
    const subSteps = action.subSteps ?? [];
    for (const _step of subSteps) {
      _step.state = 'working';

      try {
        await Promise.all([
          lastValueFrom(
            this.metaversionService.patchActivatedMetaversion(_step.id, false),
          ),
          lastValueFrom(
            this.metaversionService.patchEnabledMetaversion(_step.id, false),
          ),
        ]);
        _step.state = 'done';
      } catch (e) {
        console.error(e);
        _step.state = 'failed';
        _step.error = (e as Error).message;
      }
    }

    return subSteps.some((_ss) => _ss.error) ? 'failed' : 'done';
  }

  async stepCancelDeployment(
    action: DeleteFirmwareAction,
  ): Promise<TerminalState> {
    const subSteps = action.subSteps ?? [];
    for (const _step of subSteps) {
      _step.state = 'working';

      try {
        await this.jobsService.cancelJob(_step.id);
        _step.state = 'done';
      } catch (e) {
        console.error(e);
        _step.state = 'failed';
        _step.error = (e as Error).message;
      }
    }

    return subSteps.some((_ss) => _ss.error) ? 'failed' : 'done';
  }

  async stepDeleteFiles(action: DeleteFirmwareAction): Promise<TerminalState> {
    try {
      await lastValueFrom(this.firmwareService.deleteFirmware(this.firmwareId));
      return 'done';
    } catch (e) {
      console.error(e);
      action.error = (e as Error).message;
      return 'failed';
    }
  }

  private isDone(): boolean {
    return this.actions.every(
      (action) => action.state === 'done' || action.state === 'failed',
    );
  }

  cancel(): void {
    this.modal.close(false);
  }

  close(): void {
    this.modal.close(true);
  }
}
